import ExcelJS from "exceljs";
import {
  buildMedicalFortuneSheetData,
  downloadMedicalTablePdf,
  ensureExtension,
  triggerBlobDownload,
} from "./medicalSheetCommon";

export const CACFP_HEADERS = [
  "No.",
  "Customer",
  "Medicaid Number",
  "Date Eligibility",
  "Date of Withdrawal",
  "Free",
  "Reduced",
  "Paid",
];

const BASE_COL_WIDTHS_PX = [56, 280, 140, 130, 140, 70, 90, 70];

export function getMaxCacfpExportYear(now = new Date()) {
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based
  return month >= 6 ? year + 1 : year;
}

export function buildCacfpSheetModel(payload) {
  const title = payload?.title || "";
  const headers = Array.isArray(payload?.headers) && payload.headers.length
    ? payload.headers
    : CACFP_HEADERS;
  const rows = (payload?.rows || []).map((row) => [
    `${row.no ?? ""}`,
    `${row.name ?? ""}`,
    `${row.medicaidNumber ?? ""}`,
    `${row.dateEligibility ?? ""}`,
    `${row.dateWithdrawal ?? ""}`,
    `${row.freeMark ?? ""}`,
    `${row.reducedMark ?? ""}`,
    `${row.paidMark ?? ""}`,
  ]);

  return {
    title,
    headers,
    rows,
    columnWidthsPx: BASE_COL_WIDTHS_PX,
    name: payload?.year ? `${payload.year - 1}-${payload.year}` : "CACFP",
  };
}

export function buildCacfpFortuneSheetData(payload) {
  return buildMedicalFortuneSheetData(buildCacfpSheetModel(payload));
}

function cacfpPdfTitle(payload) {
  const year = Number(payload?.year);
  if (Number.isFinite(year) && year > 0) {
    return `CACFP Master Eligibility List\n07/01/${year - 1}-06/30/${year} (${year - 1}-${year})`;
  }
  return `${payload?.title || ""}`.replace(/\r/g, "").trim();
}

export async function downloadCacfpPdf(payload, filename) {
  const model = buildCacfpSheetModel(payload);
  const yearRange = payload?.year ? `${payload.year - 1}-${payload.year}` : "CACFP";
  await downloadMedicalTablePdf({
    title: cacfpPdfTitle(payload),
    headers: model.headers,
    rows: model.rows,
    filename: filename || `CACFP_${yearRange}`,
    pageOptions: {
      orientation: "landscape",
      fontSize: 10,
      headFontSize: 10,
      titleFontSize: 14,
      lineColor: [51, 51, 51],
    },
    columnStyles: {
      0: { cellWidth: 36, halign: "center" },
      1: { cellWidth: 160, halign: "left" },
      2: { cellWidth: 110, halign: "center" },
      3: { cellWidth: 90, halign: "center" },
      4: { cellWidth: 100, halign: "center" },
      5: { cellWidth: 50, halign: "center" },
      6: { cellWidth: 62, halign: "center" },
      7: { cellWidth: 50, halign: "center" },
    },
  });
}

export async function downloadCacfpXlsx(payload, filename) {
  const model = buildCacfpSheetModel(payload);
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Worldshine";
  const sheet = workbook.addWorksheet(model.name || "CACFP");
  const colCount = model.headers.length;
  const border80 = {
    top: { style: "thin", color: { argb: "FF333333" } },
    left: { style: "thin", color: { argb: "FF333333" } },
    bottom: { style: "thin", color: { argb: "FF333333" } },
    right: { style: "thin", color: { argb: "FF333333" } },
  };

  sheet.mergeCells(1, 1, 1, colCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = model.title;
  titleCell.font = { bold: true, size: 16, name: "Times New Roman" };
  titleCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  for (let col = 1; col <= colCount; col += 1) {
    sheet.getCell(1, col).border = border80;
  }
  sheet.getRow(1).height = 40;

  const headerRow = sheet.getRow(2);
  model.headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 12, name: "Arial" };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border80;
  });

  model.rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    row.forEach((value, colIndex) => {
      const cell = excelRow.getCell(colIndex + 1);
      cell.value = value;
      cell.font = { size: 12, name: "Arial" };
      cell.alignment = {
        horizontal: colIndex === 0 ? "center" : "left",
        vertical: "middle",
        wrapText: true,
      };
      cell.border = border80;
    });
  });

  model.columnWidthsPx.forEach((px, index) => {
    sheet.getColumn(index + 1).width = Math.max(8, Math.round(px / 7));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const yearRange = payload?.year ? `${payload.year - 1}-${payload.year}` : "CACFP";
  triggerBlobDownload(
    buffer,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ensureExtension(filename || `CACFP_${yearRange}`, ".xlsx")
  );
}
